import React, { useEffect, useMemo, useState } from "react";

import {
  getSeguimientosClientes,
  postSeguimientoCliente,
  resolverSeguimientoCliente,
  deleteSeguimientoCliente,
} from "../helpers/rutaSeguimientosClientes";

const clean = (v) => String(v ?? "").trim();

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const nombreCliente = (cliente) => {
  if (!cliente) return "";

  return (
    cliente.razonsocial ||
    [cliente.apellido1, cliente.apellido2, cliente.nombre]
      .filter(Boolean)
      .join(" ") ||
    cliente.nombreCompleto ||
    ""
  );
};

const nombreUsuario = (usuario) => {
  if (!usuario) return "-";

  return (
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ") ||
    usuario.email ||
    "-"
  );
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const estadoColor = (estado) => {
  const e = clean(estado).toUpperCase();

  if (e === "RESUELTO") return "#198754";
  if (e === "EN_REVISION") return "#ffc107";
  if (e === "PENDIENTE") return "#dc3545";

  return "#6c757d";
};

const prioridadColor = (prioridad) => {
  const p = clean(prioridad).toUpperCase();
  return p === "ALTA" ? "#dc3545" : "#6c757d";
};

export default function ModalSeguimientoCliente({
  show,
  onHide,
  venta,
  onSaved,
  soloLectura = false,
}) {
  const clienteCompra = show ? venta?.clienteCompra || null : null;

  const clienteId = show ? getId(clienteCompra) : "";

  const nroVentaTxt =
    venta?.nroventa ||
    venta?.nroVenta ||
    venta?.numero ||
    venta?.codventa ||
    venta?.nro ||
    "-";

  const ventaId = show ? getId(venta) : "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [seguimientos, setSeguimientos] = useState([]);
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState("PENDIENTE");
  const [prioridad, setPrioridad] = useState("NORMAL");

  const clienteNombre = useMemo(
    () => nombreCliente(clienteCompra),
    [clienteCompra]
  );

  const pendientes = useMemo(
    () =>
      seguimientos.filter(
        (s) => clean(s.estado).toUpperCase() !== "RESUELTO"
      ).length,
    [seguimientos]
  );

  const cargarSeguimientos = async () => {
    if (!clienteId) {
      setSeguimientos([]);
      return;
    }

    try {
      setLoading(true);

      const resp = await getSeguimientosClientes({
        clienteCompra: clienteId,
        activo: "true",
        limit: 100,
      });

      setSeguimientos(Array.isArray(resp?.seguimientos) ? resp.seguimientos : []);
    } catch (err) {
      console.error("Error cargando seguimientos:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudieron cargar los seguimientos."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !clienteId) return;

    setTexto("");
    setEstado("PENDIENTE");
    setPrioridad("NORMAL");
    cargarSeguimientos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, clienteId]);

  const guardar = async () => {
    if (soloLectura) {
      alert(
        "Esta venta pertenece a otra sede. El seguimiento del cliente está disponible solamente para consulta."
      );
      return;
    }

    if (!clienteId) {
      alert("La venta no tiene cliente comprador.");
      return;
    }

    if (!clean(texto)) {
      alert("Escribí una anotación.");
      return;
    }

    try {
      setSaving(true);

      await postSeguimientoCliente({
        clienteCompra: clienteId,
        venta: ventaId || null,
        texto,
        estado,
        prioridad,
      });

      setTexto("");
      setEstado("PENDIENTE");
      setPrioridad("NORMAL");

      await cargarSeguimientos();

      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (err) {
      console.error("Error guardando seguimiento:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo guardar el seguimiento."
      );
    } finally {
      setSaving(false);
    }
  };

  const resolver = async (item) => {
    if (soloLectura) {
      alert(
        "El seguimiento está disponible solamente para consulta."
      );
      return;
    }

    const id = getId(item);
    if (!id) return;

    if (!window.confirm("¿Marcar este seguimiento como RESUELTO?")) return;

    try {
      setSaving(true);
      await resolverSeguimientoCliente(id);
      await cargarSeguimientos();

      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (err) {
      console.error("Error resolviendo seguimiento:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo resolver el seguimiento."
      );
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (item) => {
    if (soloLectura) {
      alert(
        "El seguimiento está disponible solamente para consulta."
      );
      return;
    }

    const id = getId(item);
    if (!id) return;

    if (!window.confirm("¿Eliminar este seguimiento?")) return;

    try {
      setSaving(true);
      await deleteSeguimientoCliente(id);
      await cargarSeguimientos();

      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (err) {
      console.error("Error eliminando seguimiento:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo eliminar el seguimiento."
      );
    } finally {
      setSaving(false);
    }
  };

  const cerrar = () => {
    if (saving) return;
    if (typeof onHide === "function") onHide();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: show ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "min(1150px, 100%)",
          maxHeight: "92vh",
          overflow: "hidden",
          borderRadius: 10,
          background: "#0b1627",
          color: "#fff",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
          display: "flex",
          flexDirection: "column",
          border: "1px solid rgba(255,255,255,.15)",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#111827",
            borderBottom: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <h5 style={{ margin: 0, fontSize: 16 }}>Seguimiento del cliente</h5>

          <button
            type="button"
            onClick={cerrar}
            disabled={saving}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,.6)",
              background: "transparent",
              color: "#fff",
              fontWeight: 900,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: 16,
            overflowY: "auto",
            flex: 1,
          }}
        >
          <div
            style={{
              background: "#11233d",
              borderRadius: 8,
              padding: 14,
              marginBottom: 14,
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 11, opacity: 0.65 }}>Cliente</div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                {clienteNombre || "-"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, opacity: 0.65 }}>Venta</div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                N° {nroVentaTxt}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, opacity: 0.65 }}>Pendientes</div>
              <div style={{ fontWeight: 900, fontSize: 16, color: "#ffc107" }}>
                {pendientes}
              </div>
            </div>
          </div>

          {soloLectura && (
            <div
              style={{
                background: "#0d6efd22",
                border: "1px solid #0d6efd",
                borderRadius: 8,
                padding: 10,
                marginBottom: 14,
                color: "#dbeafe",
                fontSize: 13,
              }}
            >
              Esta venta pertenece a otra sede. El historial del cliente puede consultarse, pero no modificarse.
            </div>
          )}

          {!soloLectura && (
            <div
              style={{
                border: "1px solid #0d6efd",
                borderRadius: 8,
                padding: 14,
                marginBottom: 14,
              }}
            >
              <label style={{ display: "block", marginBottom: 6 }}>
                Nueva anotación
              </label>

              <textarea
                rows={3}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Ej: Cliente llamó consultando por financiera, revisar documentación, enviar WhatsApp..."
                disabled={saving}
                style={{
                  width: "100%",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  padding: 8,
                  resize: "vertical",
                }}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 8,
                  marginTop: 10,
                }}
              >
                <div>
                  <label style={{ display: "block", marginBottom: 4 }}>
                    Estado
                  </label>

                  <select
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    disabled={saving}
                    style={{
                      width: "100%",
                      height: 34,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      padding: "4px 8px",
                    }}
                  >
                    <option value="PENDIENTE">PENDIENTE</option>
                    <option value="EN_REVISION">EN REVISIÓN</option>
                    <option value="RESUELTO">RESUELTO</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4 }}>
                    Prioridad
                  </label>

                  <select
                    value={prioridad}
                    onChange={(e) => setPrioridad(e.target.value)}
                    disabled={saving}
                    style={{
                      width: "100%",
                      height: 34,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      padding: "4px 8px",
                    }}
                  >
                    <option value="NORMAL">NORMAL</option>
                    <option value="ALTA">ALTA</option>
                  </select>
                </div>

                <div style={{ display: "flex", alignItems: "end" }}>
                  <button
                    type="button"
                    onClick={guardar}
                    disabled={saving || !clienteId}
                    style={{
                      width: "100%",
                      height: 34,
                      borderRadius: 6,
                      border: "none",
                      background: "#198754",
                      color: "#fff",
                      fontWeight: 800,
                      cursor: saving || !clienteId ? "not-allowed" : "pointer",
                    }}
                  >
                    {saving ? "Guardando..." : "+ Guardar seguimiento"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ fontWeight: 800, marginBottom: 8 }}>
            Historial del cliente
          </div>

          <div
            style={{
              border: "1px solid rgba(255,255,255,.18)",
              borderRadius: 8,
              overflow: "hidden",
              background: "#081120",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "130px 100px 90px 1fr 150px 90px 140px",
                background: "#f8fafc",
                color: "#111827",
                fontSize: 11,
                fontWeight: 900,
                borderBottom: "1px solid #d1d5db",
              }}
            >
              <div style={{ padding: 6 }}>Fecha</div>
              <div style={{ padding: 6 }}>Estado</div>
              <div style={{ padding: 6 }}>Prioridad</div>
              <div style={{ padding: 6 }}>Detalle</div>
              <div style={{ padding: 6 }}>Usuario</div>
              <div style={{ padding: 6 }}>Venta</div>
              <div style={{ padding: 6, textAlign: "center" }}>Acciones</div>
            </div>

            {loading ? (
              <div style={{ padding: 18, textAlign: "center" }}>
                Cargando...
              </div>
            ) : seguimientos.length === 0 ? (
              <div style={{ padding: 18, textAlign: "center" }}>
                Este cliente todavía no tiene seguimiento.
              </div>
            ) : (
              seguimientos.map((s) => (
                <div
                  key={getId(s)}
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "130px 100px 90px 1fr 150px 90px 140px",
                    borderTop: "1px solid rgba(255,255,255,.12)",
                    fontSize: 12,
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 7 }}>{formatDateTime(s.createdAt)}</div>

                  <div style={{ padding: 7 }}>
                    <span
                      style={{
                        background: estadoColor(s.estado),
                        color: "#fff",
                        padding: "3px 7px",
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 900,
                      }}
                    >
                      {String(s.estado || "").replace("_", " ")}
                    </span>
                  </div>

                  <div style={{ padding: 7 }}>
                    <span
                      style={{
                        background: prioridadColor(s.prioridad),
                        color: "#fff",
                        padding: "3px 7px",
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 900,
                      }}
                    >
                      {s.prioridad || "NORMAL"}
                    </span>
                  </div>

                  <div style={{ padding: 7, whiteSpace: "pre-wrap" }}>
                    {s.texto}
                  </div>

                  <div style={{ padding: 7 }}>{nombreUsuario(s.usuario)}</div>

                  <div style={{ padding: 7 }}>N° {s.venta?.nroventa || "-"}</div>
                  
                  <div style={{ padding: 7, textAlign: "center" }}>
                    {soloLectura ? (
                      <span
                        style={{
                          fontSize: 11,
                          opacity: 0.7,
                        }}
                      >
                        Solo lectura
                      </span>
                    ) : (
                      <>
                        {s.estado !== "RESUELTO" && (
                          <button
                            type="button"
                            onClick={() => resolver(s)}
                            disabled={saving}
                            style={{
                              marginRight: 6,
                              borderRadius: 5,
                              border: "1px solid #198754",
                              background: "transparent",
                              color: "#198754",
                              fontWeight: 900,
                            }}
                          >
                            ✓
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => eliminar(s)}
                          disabled={saving}
                          style={{
                            borderRadius: 5,
                            border: "1px solid #dc3545",
                            background: "transparent",
                            color: "#dc3545",
                            fontWeight: 900,
                          }}
                        >
                          🗑
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid rgba(255,255,255,0.15)",
            display: "flex",
            justifyContent: "flex-end",
            background: "#0b1627",
          }}
        >
          <button
            type="button"
            onClick={cerrar}
            disabled={saving}
            style={{
              borderRadius: 6,
              border: "none",
              background: "#6c757d",
              color: "#fff",
              padding: "7px 14px",
              fontWeight: 800,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}